import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { RoundedBoxGeometry } from "three-stdlib";
import * as THREE from "three";
import { PALETTE } from "../palette";
import { useToonMaterial } from "../materials/useToonMaterial";
import { useGlassMaterial } from "../materials/glassMaterial";
import { Outlined } from "../materials/Outlined";
import { useSettingsStore } from "../../app/settingsStore";

/**
 * Shared 1970s-industrial vocabulary: warm-grey plastic chassis, chunky
 * tactile switches, bouncy analog dials, glass tubes. Every hardware piece
 * is assembled from these so the kit reads as one product line.
 */

interface ChassisProps {
  width?: number;
  height?: number;
  depth?: number;
  color?: string;
  position?: [number, number, number];
}

/** Rounded warm-grey plastic body — the base of most hardware. */
export function Chassis({
  width = 0.8,
  height = 0.4,
  depth = 0.8,
  color = PALETTE.chassisGrey,
  position = [0, 0, 0],
}: ChassisProps) {
  // The visible mesh and the outline hull MUST share one geometry. A plain
  // BoxGeometry has split normals at every corner, so pushing verts along
  // them flares the hull into visible plates; RoundedBoxGeometry's normals
  // are continuous, which is what makes the inverted hull read as a line.
  const geometry = useMemo(
    () => new RoundedBoxGeometry(width, height, depth, 3, 0.06),
    [width, height, depth],
  );
  const material = useToonMaterial(color);

  return (
    <group position={position}>
      <Outlined geometry={geometry} thickness={0.018}>
        <mesh geometry={geometry} material={material} castShadow receiveShadow />
      </Outlined>
    </group>
  );
}

interface DialProps {
  position?: [number, number, number];
  /** 0..1 — where the needle rests. Springs toward this value. */
  value?: number;
  color?: string;
}

/** Analog dial with a spring-damped needle that overshoots then settles. */
export function Dial({ position = [0, 0, 0], value = 0.5, color = PALETTE.cream }: DialProps) {
  const needleRef = useRef<THREE.Group>(null);
  const angle = useRef(0);
  const velocity = useRef(0);
  const faceMaterial = useToonMaterial(color);
  const needleMaterial = useToonMaterial(PALETTE.terracotta);

  useFrame((_, delta) => {
    const needle = needleRef.current;
    if (!needle) return;
    // Needle sweeps across ~240 degrees of dial face.
    const target = (value - 0.5) * Math.PI * 1.33;
    const stiffness = 90;
    const damping = 9;
    const accel = (target - angle.current) * stiffness - velocity.current * damping;
    velocity.current += accel * Math.min(delta, 0.05);
    angle.current += velocity.current * Math.min(delta, 0.05);
    needle.rotation.z = angle.current;
  });

  return (
    <group position={position} rotation={[-Math.PI / 2, 0, 0]}>
      <mesh castShadow>
        <cylinderGeometry args={[0.13, 0.13, 0.04, 20]} />
        <primitive object={faceMaterial} attach="material" />
      </mesh>
      <group ref={needleRef} position={[0, 0.03, 0]}>
        <mesh position={[0, 0, -0.05]} rotation={[Math.PI / 2, 0, 0]}>
          <boxGeometry args={[0.02, 0.1, 0.015]} />
          <primitive object={needleMaterial} attach="material" />
        </mesh>
      </group>
    </group>
  );
}

interface ToggleSwitchProps {
  position?: [number, number, number];
  on?: boolean;
}

/** Chunky tactile switch that physically throws between states. */
export function ToggleSwitch({ position = [0, 0, 0], on = false }: ToggleSwitchProps) {
  const leverRef = useRef<THREE.Group>(null);
  const baseMaterial = useToonMaterial(PALETTE.chassisGreyDark);
  const leverMaterial = useToonMaterial(on ? PALETTE.mustard : PALETTE.creamDark);

  useFrame((_, delta) => {
    const lever = leverRef.current;
    if (!lever) return;
    const target = on ? -0.5 : 0.5;
    lever.rotation.x += (target - lever.rotation.x) * Math.min(delta * 14, 1);
  });

  return (
    <group position={position}>
      <mesh castShadow>
        <boxGeometry args={[0.12, 0.05, 0.16]} />
        <primitive object={baseMaterial} attach="material" />
      </mesh>
      <group ref={leverRef} position={[0, 0.03, 0]}>
        <mesh position={[0, 0.05, 0]} castShadow>
          <boxGeometry args={[0.07, 0.11, 0.05]} />
          <primitive object={leverMaterial} attach="material" />
        </mesh>
      </group>
    </group>
  );
}

interface GlassTubeProps {
  position?: [number, number, number];
  radius?: number;
  height?: number;
  /** Fill level 0..1 and the reagent color inside. */
  fill?: number;
  fluidColor?: string;
}

/** Stylized glass tube: faked with a fresnel rim, never physical transmission. */
export function GlassTube({
  position = [0, 0, 0],
  radius = 0.16,
  height = 0.5,
  fill = 0,
  fluidColor = PALETTE.fluidSkyBlue,
}: GlassTubeProps) {
  const glassMaterial = useGlassMaterial();
  const fluidMaterial = useToonMaterial(fluidColor, { emissive: fluidColor, emissiveIntensity: 0.35 });
  const fillHeight = Math.max(0.001, height * fill * 0.9);

  return (
    <group position={position}>
      {fill > 0 && (
        <mesh position={[0, -height / 2 + fillHeight / 2 + 0.02, 0]} castShadow>
          <cylinderGeometry args={[radius * 0.86, radius * 0.86, fillHeight, 16]} />
          <primitive object={fluidMaterial} attach="material" />
        </mesh>
      )}
      <mesh>
        <cylinderGeometry args={[radius, radius, height, 16, 1, true]} />
        <primitive object={glassMaterial} attach="material" />
      </mesh>
      {/* Metal collars top and bottom so the tube reads as fitted hardware. */}
      <Collar position={[0, height / 2, 0]} radius={radius} />
      <Collar position={[0, -height / 2, 0]} radius={radius} />
    </group>
  );
}

function Collar({ position, radius }: { position: [number, number, number]; radius: number }) {
  // Mustard, not dark grey: heavy dark collars read as black caps at this
  // camera distance and visually decapitate the glassware.
  const material = useToonMaterial(PALETTE.mustardDark);
  return (
    <mesh position={position} castShadow>
      <cylinderGeometry args={[radius * 1.12, radius * 1.12, 0.05, 16]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
}

/**
 * Hazard tells (spec 001 task 5). Each of the five hazards
 * (`render/hazardTells.ts`) gets its own shape here so the reading survives a
 * greyscale capture: a needle position, a count of raised tabs, a receding
 * plate, a stack of pellets, a closing wedge. Colour reinforces each one but
 * is never the only channel — CLAUDE.md's "reagents are shapes" rule extends
 * to hazards for the same reason: it is not readable for everyone, and reading
 * a moving board at a glance is the whole point of a tell.
 *
 * The shadow/glow degrade is the piece of these that "cannot degrade
 * gracefully" without a tier check: a busy board can put a segment bar on
 * every backed-up pipe and a pellet stack on every catalyst bed at once, and
 * every one of those small meshes casting a shadow is the kind of per-object
 * cost that made the post stack and dust motes tier-gated already. The count,
 * length and sweep that carry the actual reading are never gated — only the
 * shadow and the emissive kick are.
 */
function useHazardRenderTier() {
  const quality = useSettingsStore((s) => s.quality);
  return {
    castShadow: quality !== "low",
    glow: quality === "high" ? 0.4 : quality === "medium" ? 0.22 : 0,
  };
}

interface SegmentBarProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  /** How many segments are lit, out of `total`. Clamped into range. */
  filled: number;
  total: number;
  color?: string;
}

/**
 * A row of chunky tabs, lit ones standing proud of the rail and unlit ones
 * flush with it. The count of raised tabs is the reading — pressure against
 * its burst limit — so it survives greyscale where a colour fill would not.
 */
export function SegmentBar({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  filled,
  total,
  color = PALETTE.hazardWarning,
}: SegmentBarProps) {
  const { castShadow, glow } = useHazardRenderTier();
  const segments = Math.max(Math.round(total), 1);
  const lit = Math.max(0, Math.min(Math.round(filled), segments));
  const spacing = 0.09;
  const offset = ((segments - 1) * spacing) / 2;
  const litMaterial = useToonMaterial(color, { emissive: color, emissiveIntensity: glow });
  const dimMaterial = useToonMaterial(PALETTE.chassisGreyDark);

  return (
    <group position={position} rotation={rotation}>
      {Array.from({ length: segments }, (_, i) => {
        const isLit = i < lit;
        return (
          <mesh key={i} position={[i * spacing - offset, isLit ? 0.028 : 0, 0]} castShadow={castShadow}>
            <boxGeometry args={[0.06, 0.05, 0.05]} />
            <primitive object={isLit ? litMaterial : dimMaterial} attach="material" />
          </mesh>
        );
      })}
    </group>
  );
}

interface WearBarProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  /** 0..1 — full length at max HP, nothing left at 0. */
  fraction: number;
  length?: number;
  color?: string;
}

/**
 * A plate that recedes from a fixed dark track as it wears — continuous,
 * not counted, so it never reads as the same shape as `SegmentBar`'s tabs.
 * Wall HP has no upper "limit" to sweep toward, only a length to lose.
 */
export function WearBar({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  fraction,
  length = 0.3,
  color = PALETTE.cream,
}: WearBarProps) {
  const { castShadow } = useHazardRenderTier();
  const clamped = Math.max(0, Math.min(fraction, 1));
  const width = Math.max(length * clamped, 0.015);
  const trackMaterial = useToonMaterial(PALETTE.chassisGreyDark);
  const barMaterial = useToonMaterial(color);

  return (
    <group position={position} rotation={rotation}>
      <mesh castShadow={castShadow}>
        <boxGeometry args={[length, 0.035, 0.05]} />
        <primitive object={trackMaterial} attach="material" />
      </mesh>
      <mesh position={[-(length - width) / 2, 0, 0.001]} castShadow={castShadow}>
        <boxGeometry args={[width, 0.045, 0.055]} />
        <primitive object={barMaterial} attach="material" />
      </mesh>
    </group>
  );
}

interface PelletStackProps {
  position?: [number, number, number];
  /** How many pellets are lit, out of `total`. Clamped into range. */
  filled: number;
  total: number;
  color?: string;
}

/**
 * A vertical column of pellets, stacked bottom-up. The catalyst bed comment
 * has long promised "a stack of pellets behind glass" — this is the hazard's
 * own reading of that stack, counted rather than tinted, so catalyst health
 * is legible without relying on the bed's desaturation.
 */
export function PelletStack({ position = [0, 0, 0], filled, total, color = PALETTE.fluidAmber }: PelletStackProps) {
  const { castShadow, glow } = useHazardRenderTier();
  const segments = Math.max(Math.round(total), 1);
  const lit = Math.max(0, Math.min(Math.round(filled), segments));
  const spacing = 0.075;
  const litMaterial = useToonMaterial(color, { emissive: color, emissiveIntensity: glow });
  const dimMaterial = useToonMaterial(PALETTE.chassisGreyDark);

  return (
    <group position={position}>
      {Array.from({ length: segments }, (_, i) => {
        const isLit = i < lit;
        return (
          <mesh key={i} position={[0, i * spacing, 0]} castShadow={castShadow}>
            <cylinderGeometry args={[0.05, 0.05, 0.045, 10]} />
            <primitive object={isLit ? litMaterial : dimMaterial} attach="material" />
          </mesh>
        );
      })}
    </group>
  );
}

interface ProgressArcProps {
  position?: [number, number, number];
  /** 0..1 toward the limit. A sliver still shows at 0 so the ring reads as a
   * dial rather than vanishing at the first tick. */
  fraction: number;
  radius?: number;
  color?: string;
}

/**
 * A wedge that sweeps closed like a clock face as ticks accrue toward
 * sludging. Flat and radial rather than a needle or a bar, and mounted with
 * the same face-up rotation as `Dial` so it reads the same way from the
 * fixed isometric camera.
 */
export function ProgressArc({ position = [0, 0, 0], fraction, radius = 0.13, color = PALETTE.sage }: ProgressArcProps) {
  const { glow } = useHazardRenderTier();
  const clamped = Math.max(0.03, Math.min(fraction, 1));
  const trackMaterial = useToonMaterial(PALETTE.chassisGreyDark);
  const fillMaterial = useToonMaterial(color, { emissive: color, emissiveIntensity: glow });

  return (
    <group position={position} rotation={[-Math.PI / 2, 0, 0]}>
      <mesh>
        <ringGeometry args={[radius * 0.55, radius, 24]} />
        <primitive object={trackMaterial} attach="material" />
      </mesh>
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <ringGeometry args={[radius * 0.55, radius, 24, 1, 0, clamped * Math.PI * 2]} />
        <primitive object={fillMaterial} attach="material" />
      </mesh>
    </group>
  );
}

interface ForecastPinProps {
  position?: [number, number, number];
  /** 0-based tick the crossing lands on; drives how urgently the pin bobs. */
  tickInTurn: number;
  ticksPerTurn: number;
}

/**
 * Spec 0018's board badge: a surveyor's pin planted on hardware the coming turn
 * will push past a limit. It is deliberately unlike every other tell in this
 * kit — a stalk and a faceted head, floating clear above the chassis — because
 * it reports the *future*, and a player who mistakes it for a live counter has
 * misread the board.
 *
 * The shape carries the meaning, as with reagents; the warning tint only
 * reinforces it. The pin never says which card to play, and its lean says
 * *when*, not *what*: an imminent crossing bobs fast, a distant one drifts.
 */
export function ForecastPin({ position = [0, 0, 0], tickInTurn, ticksPerTurn }: ForecastPinProps) {
  const groupRef = useRef<THREE.Group>(null);
  const { castShadow, glow } = useHazardRenderTier();
  const headMaterial = useToonMaterial(PALETTE.hazardWarning, {
    emissive: PALETTE.hazardWarning,
    emissiveIntensity: glow,
  });
  const stalkMaterial = useToonMaterial(PALETTE.chassisGreyDark);

  // Earliest crossings bob at roughly twice the rate of the latest ones, so a
  // board with several pins up reads as an order without needing a number.
  const urgency = ticksPerTurn > 0 ? 1 - Math.min(tickInTurn / ticksPerTurn, 1) : 1;
  const speed = 2 + urgency * 2;

  useFrame((clockState) => {
    const group = groupRef.current;
    if (!group) return;
    group.position.y = Math.sin(clockState.clock.elapsedTime * speed) * 0.035;
    group.rotation.y = clockState.clock.elapsedTime * 0.8;
  });

  return (
    <group position={position}>
      <group ref={groupRef}>
        <mesh castShadow={castShadow}>
          <cylinderGeometry args={[0.012, 0.012, 0.22, 6]} />
          <primitive object={stalkMaterial} attach="material" />
        </mesh>
        <mesh position={[0, 0.16, 0]} castShadow={castShadow}>
          <octahedronGeometry args={[0.075, 0]} />
          <primitive object={headMaterial} attach="material" />
        </mesh>
      </group>
    </group>
  );
}
